/**
 * CSV do resultado, gerado aqui no navegador.
 *
 * A API não tem rota de exportação de relatório, e não precisa: diferente da
 * pesquisa de conteúdo — que é paginada com teto de 100 e por isso ganhou rota
 * própria —, o relatório já devolve **tudo numa resposta só**. O dado inteiro
 * está em mãos; falta só escrever.
 *
 * Sai o resultado **inteiro**, não as 200 linhas desenhadas. É o ponto: quem
 * baixa quer o que não coube na tela.
 *
 * O escape é o mesmo da exportação de conteúdo, inclusive a proteção contra
 * fórmula — um texto começando com `=` vira fórmula ao abrir no Excel, e aqui
 * há nome de cliente e texto de busca digitados por gente de fora.
 */

import type { Coluna, Relatorio } from "./catalogo"

export function baixarCsv(relatorio: Relatorio, resposta: Record<string, unknown>) {
  const partes: string[] = []
  const s = relatorio.saida

  if (s.forma === "tiles") {
    partes.push(linha(["Indicador", "Valor"]))
    for (const c of s.campos) partes.push(linha([c.rotulo, String(resposta[c.chave] ?? "")]))
  } else if (s.forma === "series") {
    for (const serie of s.series) {
      partes.push(linha([serie.rotulo]))
      partes.push(...tabela(arrayEm(resposta, serie.chave), serie.colunas))
      partes.push("")
    }
  } else if (s.forma === "composta") {
    for (const b of s.blocos) {
      partes.push(linha([b.rotulo]))
      partes.push(...tabela(arrayEm(resposta, b.de), b.colunas))
      partes.push("")
    }
  } else {
    partes.push(...tabela(arrayEm(resposta, s.de ?? ""), s.colunas))
  }

  // BOM: sem ele o Excel em português abre o UTF-8 como Latin-1 e come os acentos.
  const texto = "﻿" + partes.join("\r\n")
  const arquivo = new Blob([texto], { type: "text/csv;charset=utf-8" })

  const nome = `${relatorio.chave}_${new Date().toISOString().slice(0, 10)}.csv`
  const url = URL.createObjectURL(arquivo)
  const a = document.createElement("a")
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function tabela(linhas: Record<string, unknown>[], colunas: Coluna[]): string[] {
  const saida = [linha(colunas.map((c) => c.rotulo))]
  for (const l of linhas) saida.push(linha(colunas.map((c) => valor(l[c.chave]))))
  return saida
}

function linha(celulas: string[]): string {
  return celulas.map(celula).join(";")
}

function valor(v: unknown): string {
  if (v === null || v === undefined) return ""
  if (typeof v === "boolean") return v ? "sim" : "não"
  return String(v)
}

function celula(valor: string): string {
  if (!valor) return ""

  let texto = valor
  // Neutraliza a injeção de fórmula, como faz a exportação de conteúdo na API.
  if ("=+-@\t\r".includes(texto[0]!)) texto = "'" + texto

  return /[;"\r\n]/.test(texto) ? `"${texto.replace(/"/g, '""')}"` : texto
}

function arrayEm(objeto: Record<string, unknown>, caminho: string): Record<string, unknown>[] {
  if (!caminho) {
    const direto = objeto[""]
    if (Array.isArray(direto)) return direto as Record<string, unknown>[]
  }
  let atual: unknown = objeto
  for (const parte of caminho.split(".")) {
    if (!parte) continue
    if (atual && typeof atual === "object") atual = (atual as Record<string, unknown>)[parte]
    else return []
  }
  return Array.isArray(atual) ? (atual as Record<string, unknown>[]) : []
}
